import { useFormContext } from 'react-hook-form';
// Types
import { FormInputs } from 'components/CreatorSetup/StreamingSetup/CreateStreamSetup/CreateStreamSetup';
// Hooks
import { useEstimatedStreamProfit } from 'hooks';
// Helpers
import { calculateAffiliateCommission, roundMoney } from 'helpers/affiliate';
import { formatPriceToUSD } from 'helpers/priceText';
// Ui
import Text from 'ui3/Text/Text';
import Divider from 'ui3/Divider/Divider';
// Styles
import styles from './StreamSummaryPanel.module.scss';

const EMPTY = '—';

type SummaryRowProps = {
  label: string;
  value: string;
};

const SummaryRow = ({ label, value }: SummaryRowProps) => (
  <div className={styles.row}>
    <Text variant="body2Regular" color="lights-low">
      {label}
    </Text>
    <Text variant="body2Medium" className={styles.value}>
      {value}
    </Text>
  </div>
);

/**
 * Right-hand "SUMMARY" card of the create wizard. Every amount is the
 * organizer's net (after the platform fee), the same base the backend uses to
 * snapshot the commission on each order.
 */
const StreamSummaryPanel = (): JSX.Element => {
  const { watch } = useFormContext<FormInputs>();

  const isFree = Boolean(watch('isFree'));
  const price = Number(watch('price'));
  const hasPrice = !isFree && price > 0;
  const netProfit = useEstimatedStreamProfit(price, isFree);

  const affiliateEnabled = Boolean(watch('affiliateEnabled')) && !isFree;
  const commissionPercent = Number(watch('affiliateCommissionPercent'));
  const hasCommission = affiliateEnabled && commissionPercent > 0;
  const participantEarns = calculateAffiliateCommission(
    netProfit,
    commissionPercent
  );
  const organizerKeeps = roundMoney(netProfit - participantEarns);

  const streamPrice = isFree
    ? 'Free Stream'
    : hasPrice
    ? formatPriceToUSD(price)
    : EMPTY;
  const totalPerSale = !hasPrice
    ? EMPTY
    : hasCommission
    ? `${formatPriceToUSD(organizerKeeps)} / affiliate sale`
    : formatPriceToUSD(netProfit);

  return (
    <div className={styles.root}>
      <Text variant="h6">SUMMARY</Text>

      <div className={styles.rows}>
        <SummaryRow label="Stream Price" value={streamPrice} />
        <SummaryRow
          label="Affiliate program"
          value={affiliateEnabled ? 'On' : 'Off'}
        />
        <SummaryRow
          label="Commission"
          value={hasCommission ? `${commissionPercent}%` : EMPTY}
        />
        <SummaryRow
          label="Participant earns"
          value={
            hasCommission
              ? `${formatPriceToUSD(participantEarns)} / sale`
              : EMPTY
          }
        />
        <SummaryRow label="Total per sale" value={totalPerSale} />
      </div>

      <Divider />

      <Text variant="captionRegular" color="lights-low">
        Every Participant you invite gets a unique affiliate link automatically.
        Refunded or cancelled purchases don&apos;t count toward earnings.
      </Text>
    </div>
  );
};

export default StreamSummaryPanel;
